/**
 * Phase 170 — External Signal Ingest & Autonomous Dispatcher Panel
 *
 * Shows the inbound webhook URL + token, rule matrix, recent signal log,
 * and a synthetic test-fire button. Mounted right below the Phase 169
 * AutoPilot panel on `/super-admin/announce`.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Satellite, RefreshCw, Copy, Loader2, ShieldCheck, RotateCw,
  CheckCircle2, AlertTriangle, Bug, EyeOff, Inbox, ArrowUp, ArrowDown, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_PILL = {
  dispatched:        'bg-emerald-100 text-emerald-700 border-emerald-200',
  queued:            'bg-blue-100 text-blue-700 border-blue-200',
  dry_run:           'bg-violet-100 text-violet-700 border-violet-200',
  deduped:           'bg-amber-100 text-amber-700 border-amber-200',
  ignored_no_match:  'bg-slate-100 text-slate-600 border-slate-200',
  failed:            'bg-rose-100 text-rose-700 border-rose-200',
  received:          'bg-slate-100 text-slate-600 border-slate-200',
};

export default function ExternalSignalsPanel() {
  const [settings, setSettings] = useState(null);
  const [rules, setRules] = useState([]);
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testInput, setTestInput] = useState({ source: 'IMD_SCRAPER', severity: 'RED_ALERT', state: 'Andhra Pradesh', city: '' });
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [tokenVisible, setTokenVisible] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r, l] = await Promise.all([
        api.get('/admin/broadcast/signal-settings').catch(() => ({ data: null })),
        api.get('/admin/broadcast/signal-rules').catch(() => ({ data: null })),
        api.get('/admin/broadcast/signals-log?limit=10').catch(() => ({ data: null })),
      ]);
      setSettings(s.data);
      setRules(r.data?.rules || []);
      setLog(l.data);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleEnabled = async (next) => {
    try {
      const r = await api.put('/admin/broadcast/signal-settings', {
        enabled: !!next,
        daily_limit_per_ip: settings?.daily_limit_per_ip ?? 200,
        rotate_token: false,
      });
      setSettings(r.data);
      toast.success(next ? 'External signal receiver enabled' : 'Receiver disabled');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Toggle failed');
    }
  };

  const rotateToken = async () => {
    if (!window.confirm('Rotate the signal token? Any external partner using the current token will start getting 401 until you re-share the new one.')) return;
    setRotating(true);
    try {
      const r = await api.put('/admin/broadcast/signal-settings', {
        enabled: !!settings?.enabled,
        daily_limit_per_ip: settings?.daily_limit_per_ip ?? 200,
        rotate_token: true,
      });
      setSettings(r.data);
      toast.success('Token rotated. Update partner configurations now.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rotate failed');
    } finally {
      setRotating(false);
    }
  };

  const copy = (val, label) => {
    if (!val) return;
    navigator.clipboard.writeText(val);
    toast.success(`${label} copied to clipboard`);
  };

  const toggleRule = async (rule) => {
    try {
      await api.post('/admin/broadcast/signal-rules', {
        ...rule, enabled: !rule.enabled,
      });
      setRules((rs) => rs.map((r) => (r.id === rule.id ? { ...r, enabled: !r.enabled } : r)));
      toast.success(`Rule ${rule.name} ${!rule.enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Could not update rule');
    }
  };

  const reorderRules = async (idx, delta) => {
    const next = [...rules];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setRules(next); // optimistic
    try {
      await api.put('/admin/broadcast/signal-rules/reorder', {
        ordered_ids: next.map((r) => r.id),
      });
    } catch (err) {
      toast.error('Could not reorder rules');
      load();
    }
  };

  const formatRelativeAge = (iso) => {
    if (!iso) return 'never';
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60 * 1000) return 'just now';
    if (ms < 60 * 60 * 1000) return `${Math.floor(ms / 60000)} min ago`;
    if (ms < 24 * 60 * 60 * 1000) return `${Math.floor(ms / 3600000)} h ago`;
    return `${Math.floor(ms / 86400000)} d ago`;
  };

  const runTest = async () => {
    setTestLoading(true);
    setTestResult(null);
    try {
      const r = await api.post('/admin/broadcast/signal-test', testInput);
      setTestResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Test failed');
    } finally {
      setTestLoading(false);
    }
  };

  const summary = log?.summary || {};

  return (
    <Card
      className="border-sky-200 bg-gradient-to-br from-sky-50/40 via-white to-cyan-50/30"
      data-testid="phase170-signals-panel"
    >
      <CardContent className="p-5 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-600 flex items-center justify-center">
              <Satellite className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-bold text-base sm:text-lg text-slate-900" data-testid="phase170-title">
                  External Signal Auto-Trigger
                </h2>
                <Badge className="bg-sky-100 text-sky-800 border-sky-200 text-[9px] font-bold uppercase tracking-widest">Phase 170</Badge>
                {settings?.enabled === false && (
                  <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[9px]">
                    RECEIVER DISABLED
                  </Badge>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">
                Authenticated public webhook for external monitoring partners (IMD scrapers,
                UptimeRobot, weather APIs). Inbound signals match a rule, the AI Copilot drafts the
                announcement, the platform fans out — &lt;90s from signal to coach's pocket.
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} className="h-8 text-xs" data-testid="phase170-refresh">
            {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
            Refresh
          </Button>
        </div>

        {/* Webhook + token strip */}
        <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-2.5"
          data-testid="phase170-webhook-strip">
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <div className="flex items-center gap-2">
              <Switch
                checked={!!settings?.enabled}
                onCheckedChange={toggleEnabled}
                data-testid="phase170-toggle-enabled"
              />
              <span className="text-xs font-semibold text-slate-700">
                Receiver
                <span className="ml-1.5 text-[10px] text-slate-400">
                  {settings?.enabled ? 'ACTIVE' : 'OFF'}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-slate-500">
              <span>Daily IP cap: <strong className="text-slate-700">{settings?.daily_limit_per_ip ?? '—'}</strong></span>
              <Button variant="outline" size="sm" onClick={rotateToken}
                disabled={rotating}
                className="h-7 text-[10px]"
                data-testid="phase170-rotate-token">
                {rotating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RotateCw className="h-3 w-3 mr-1" />}
                Rotate token
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-2">
            <div className="lg:col-span-3">
              <Label className="text-[9px] font-bold uppercase tracking-widest text-slate-500">Webhook URL</Label>
              <div className="flex items-center gap-1 mt-1">
                <code className="text-[11px] bg-slate-50 border border-slate-200 px-2 py-1.5 rounded flex-1 truncate"
                  data-testid="phase170-webhook-url">
                  {settings?.webhook_url || '—'}
                </code>
                <Button variant="ghost" size="sm" onClick={() => copy(settings?.webhook_url, 'Webhook URL')} className="h-7 px-1.5">
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <div className="lg:col-span-2">
              <Label className="text-[9px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1">
                X-OutPlay-Signal-Token <ShieldCheck className="h-2.5 w-2.5 text-emerald-500" />
              </Label>
              <div className="flex items-center gap-1 mt-1">
                <code className="text-[11px] bg-slate-50 border border-slate-200 px-2 py-1.5 rounded flex-1 truncate font-mono"
                  data-testid="phase170-token">
                  {tokenVisible
                    ? (settings?.token || '—')
                    : ((settings?.token || '').slice(0, 11) + '••••••••••••••')}
                </code>
                <Button variant="ghost" size="sm"
                  onClick={() => setTokenVisible((v) => !v)}
                  className="h-7 px-1.5"
                  data-testid="phase170-toggle-token-vis">
                  <EyeOff className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => copy(settings?.token, 'Token')} className="h-7 px-1.5">
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Recent log strip */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Stats */}
          <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-1.5"
            data-testid="phase170-stats">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1">
              <Inbox className="h-3 w-3" /> Last 30 signals
            </div>
            <div className="grid grid-cols-3 gap-1.5 text-[10px]">
              <div className="bg-emerald-50 rounded p-1.5 text-center">
                <div className="text-base font-extrabold text-emerald-700">{summary.dispatched ?? 0}</div>
                <div className="text-emerald-700/80">dispatched</div>
              </div>
              <div className="bg-amber-50 rounded p-1.5 text-center">
                <div className="text-base font-extrabold text-amber-700">{summary.deduped ?? 0}</div>
                <div className="text-amber-700/80">deduped</div>
              </div>
              <div className="bg-slate-50 rounded p-1.5 text-center">
                <div className="text-base font-extrabold text-slate-700">{summary.ignored ?? 0}</div>
                <div className="text-slate-500">ignored</div>
              </div>
              <div className="bg-violet-50 rounded p-1.5 text-center">
                <div className="text-base font-extrabold text-violet-700">{summary.dry_runs ?? 0}</div>
                <div className="text-violet-700/80">dry-runs</div>
              </div>
              <div className="bg-blue-50 rounded p-1.5 text-center">
                <div className="text-base font-extrabold text-blue-700">{summary.queued ?? 0}</div>
                <div className="text-blue-700/80">queued</div>
              </div>
              <div className="bg-rose-50 rounded p-1.5 text-center">
                <div className="text-base font-extrabold text-rose-700">{summary.failed ?? 0}</div>
                <div className="text-rose-700/80">failed</div>
              </div>
            </div>
          </div>

          {/* Recent log table */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-3"
            data-testid="phase170-log">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                Recent inbound signals
              </div>
              <Button variant="outline" size="sm" onClick={() => setTestOpen(true)}
                className="h-7 text-[10px]"
                data-testid="phase170-test-btn">
                <Bug className="h-3 w-3 mr-1" /> Test signal
              </Button>
            </div>
            {!log?.rows?.length ? (
              <p className="text-[11px] text-slate-400">No signals received yet.</p>
            ) : (
              <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
                {(log.rows || []).slice(0, 6).map((row) => (
                  <div key={row.id} className="flex items-center gap-2 text-[10px] py-1 border-b border-slate-100 last:border-0">
                    <span className="text-slate-400 tabular-nums min-w-[88px]">
                      {(row.received_at || '').slice(11, 19)}
                    </span>
                    <Badge variant="outline"
                      className={`${STATUS_PILL[row.status] || STATUS_PILL.received} text-[8px] px-1.5 py-0`}>
                      {row.status}
                    </Badge>
                    <span className="text-slate-700 font-mono text-[9px] truncate flex-1">
                      {row.payload_snapshot?.source} · {row.payload_snapshot?.severity} · {row.payload_snapshot?.state || '—'}
                    </span>
                    {row.matched_template_id && (
                      <Badge variant="outline" className="text-[9px] bg-sky-50 text-sky-700 border-sky-200">
                        {row.matched_template_id}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Rules table */}
        <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-2"
          data-testid="phase170-rules">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
              Signal matching rules
            </div>
            <span className="text-[10px] text-slate-400">{rules.length} active rules · top-down match order</span>
          </div>
          <div className="space-y-1.5">
            {rules.map((rule, idx) => (
              <div key={rule.id}
                className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-0"
                data-testid={`phase170-rule-${rule.id}`}>
                {/* Phase 170.b — up/down reorder arrows */}
                <div className="flex flex-col -space-y-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => reorderRules(idx, -1)}
                    disabled={idx === 0}
                    className="p-0.5 disabled:opacity-30 hover:bg-slate-100 rounded"
                    title="Move up"
                    data-testid={`phase170-rule-up-${rule.id}`}
                  >
                    <ArrowUp className="h-3 w-3 text-slate-500" />
                  </button>
                  <button
                    type="button"
                    onClick={() => reorderRules(idx, 1)}
                    disabled={idx === rules.length - 1}
                    className="p-0.5 disabled:opacity-30 hover:bg-slate-100 rounded"
                    title="Move down"
                    data-testid={`phase170-rule-down-${rule.id}`}
                  >
                    <ArrowDown className="h-3 w-3 text-slate-500" />
                  </button>
                </div>
                <Switch
                  checked={!!rule.enabled}
                  onCheckedChange={() => toggleRule(rule)}
                  className="scale-75"
                />
                <span className="text-[11px] font-semibold text-slate-800 truncate flex-1">
                  {rule.name}
                </span>
                {/* Phase 170.b — last_fired_at display */}
                {rule.last_fired_at && (
                  <span className="text-[9px] text-slate-400 flex items-center gap-1 shrink-0"
                    data-testid={`phase170-rule-last-fired-${rule.id}`}>
                    <Clock className="h-2.5 w-2.5" /> {formatRelativeAge(rule.last_fired_at)}
                    {rule.fire_count ? (
                      <span className="ml-1 px-1 py-0 bg-slate-100 rounded font-bold text-slate-600">
                        ×{rule.fire_count}
                      </span>
                    ) : null}
                  </span>
                )}
                <Badge variant="outline" className="text-[9px] py-0">
                  {rule.source}
                </Badge>
                {rule.severity_match && (
                  <Badge variant="outline" className="text-[9px] py-0">
                    {rule.severity_match}
                  </Badge>
                )}
                {rule.state_match && (
                  <Badge variant="outline" className="text-[9px] py-0">
                    {rule.state_match}
                  </Badge>
                )}
                <span className="text-[9px] text-slate-400">→</span>
                <Badge className="bg-sky-100 text-sky-800 border-sky-200 text-[9px] py-0">
                  {rule.template_id}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      </CardContent>

      {/* Test-signal modal */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="max-w-md" data-testid="phase170-test-modal">
          <DialogHeader>
            <DialogTitle>Test Signal (local dry-run)</DialogTitle>
            <DialogDescription>
              Simulate an inbound webhook against the same rule-matching pipeline. Nothing is
              dispatched — only the matching rule is returned.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-semibold">Source</Label>
                <Input value={testInput.source}
                  onChange={(e) => setTestInput({ ...testInput, source: e.target.value })}
                  data-testid="phase170-test-source" className="h-8 text-xs"/>
              </div>
              <div>
                <Label className="text-xs font-semibold">Severity</Label>
                <Input value={testInput.severity}
                  onChange={(e) => setTestInput({ ...testInput, severity: e.target.value })}
                  data-testid="phase170-test-severity" className="h-8 text-xs"/>
              </div>
              <div>
                <Label className="text-xs font-semibold">State</Label>
                <Input value={testInput.state}
                  onChange={(e) => setTestInput({ ...testInput, state: e.target.value })}
                  data-testid="phase170-test-state" className="h-8 text-xs"/>
              </div>
              <div>
                <Label className="text-xs font-semibold">City <span className="font-normal text-slate-400">(optional)</span></Label>
                <Input value={testInput.city}
                  onChange={(e) => setTestInput({ ...testInput, city: e.target.value })}
                  data-testid="phase170-test-city" className="h-8 text-xs"/>
              </div>
            </div>
            <Button onClick={runTest} disabled={testLoading}
              className="w-full bg-sky-600 hover:bg-sky-700"
              data-testid="phase170-test-run">
              {testLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Bug className="h-3.5 w-3.5 mr-1" />}
              Match against rules
            </Button>
            {testResult && (
              <div className={`rounded-lg p-3 text-xs ${
                testResult.matched_rule
                  ? 'bg-emerald-50 border border-emerald-200'
                  : 'bg-amber-50 border border-amber-200'
              }`} data-testid="phase170-test-result">
                {testResult.matched_rule ? (
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />
                    <div>
                      <div className="font-bold text-emerald-900">{testResult.matched_rule.name}</div>
                      <div className="text-emerald-800 mt-0.5">
                        Would fire template{' '}
                        <code className="text-[10px] bg-white px-1 rounded">{testResult.matched_template_id}</code>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" />
                    <div className="text-amber-900">
                      No rule matches this payload. The webhook would respond
                      <code className="text-[10px] bg-white px-1 rounded mx-1">ignored</code>
                      and skip dispatch.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
